import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { WorkspaceService } from "@/features/Workspaces/services/workspaceService";
import { useQuery } from "@tanstack/react-query";
import { getApi, get } from "@/shared/utils/fetchApi";
import { API_CONFIG } from "@/infrastructure/config/api";
import { analyticsService } from "@/shared/services/analytics";
import { UserRole } from "@/shared/types/user-role";

interface Workspace {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
  organization_id: string;
}

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  products: Workspace[];
  isLoading: boolean;
  isReloading: boolean; // Loading após salvar produto
  hasInitialized: boolean; // Indica se a inicialização de produtos foi concluída
  productChangeKey: number; // Key que muda quando o produto muda, usada para forçar re-renderização
  reloadUserProducts: () => Promise<void>; // Função para recarregar produtos do usuário
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(
  undefined,
);

export { WorkspaceContext };

// Chaves usadas para armazenar dados no localStorage
const PRODUCTS_CACHE_KEY = "prodgy_cached_products";
const SELECTED_PRODUCT_KEY = "prodgy_selected_product";

// Função para salvar produtos no localStorage
const saveProductsToLocalCache = (products: Workspace[]) => {
  try {
    if (!products || products.length === 0) return;

    const cacheData = {
      products,
      timestamp: Date.now(),
    };

    localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {
    console.error(
      "[WorkspaceContext] Erro ao salvar produtos no cache local:",
      e,
    );
  }
};

// Função para carregar produtos do localStorage
const loadProductsFromLocalCache = (): Workspace[] | null => {
  try {
    const cachedData = localStorage.getItem(PRODUCTS_CACHE_KEY);
    if (!cachedData) {
      return null;
    }

    const parsedCache = JSON.parse(cachedData);
    // Verifica se o cache é recente (menos de 24 horas)
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    if (Date.now() - parsedCache.timestamp > ONE_DAY_MS) {
      return null;
    }

    return parsedCache.products;
  } catch (e) {
    console.error(
      "[WorkspaceContext] Erro ao carregar produtos do cache local:",
      e,
    );
    return null;
  }
};

// Função para salvar produto selecionado no localStorage
const saveSelectedProductToLocal = (workspace: Workspace | null) => {
  try {
    if (workspace) {
      localStorage.setItem(SELECTED_PRODUCT_KEY, JSON.stringify(workspace));
    } else {
      localStorage.removeItem(SELECTED_PRODUCT_KEY);
    }
  } catch (e) {
    // Fallback: tentar usar sessionStorage como alternativa
    try {
      if (workspace) {
        sessionStorage.setItem(SELECTED_PRODUCT_KEY, JSON.stringify(workspace));
      } else {
        sessionStorage.removeItem(SELECTED_PRODUCT_KEY);
      }
    } catch (sessionError) {
      // Se ambos falharem, não há muito o que fazer sem impactar a UX
      // O aplicativo continuará funcionando, apenas não persistirá a seleção
    }
  }
};

// Função para carregar produto selecionado do localStorage
const loadSelectedProductFromLocal = (): Workspace | null => {
  try {
    const savedProduct = localStorage.getItem(SELECTED_PRODUCT_KEY);
    if (!savedProduct) {
      return null;
    }

    const workspace = JSON.parse(savedProduct);
    return workspace;
  } catch (e) {
    // Fallback: tentar carregar do sessionStorage
    try {
      const sessionProduct = sessionStorage.getItem(SELECTED_PRODUCT_KEY);
      if (sessionProduct) {
        const workspace = JSON.parse(sessionProduct);
        return workspace;
      }
    } catch (sessionError) {
      // Se ambos falharem, retorna null - o app funcionará sem persistência
    }
    return null;
  }
};

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(
    () => {
      // Inicializar do localStorage para evitar scope 'organization' no primeiro render
      // O useEffect valida e corrige se o workspace salvo não for mais válido
      return loadSelectedProductFromLocal();
    },
  );
  // Chave que incrementa quando o produto muda para forçar re-renderização dos componentes
  const [productChangeKey, setProductChangeKey] = useState<number>(0);
  const [isReloading, setIsReloading] = useState<boolean>(false);
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const isUserOnly = user?.role?.toLowerCase() === UserRole.USER;
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  // Trocar de organização recomeça a seleção de workspace: o workspace ativo pertencia
  // à organização anterior. Vale para qualquer usuário desde o AB#84509 — antes só
  // superadmin trocava de organização, então só ele precisava disto.
  const prevOrgRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const orgId = selectedOrganization?.id;
    if (prevOrgRef.current !== undefined && prevOrgRef.current !== orgId) {
      setHasInitialized(false);
      setCurrentWorkspace(null);
      setCurrentProductId(null);
    }
    prevOrgRef.current = orgId;
  }, [selectedOrganization?.id]);

  // Armazenar uma referência ao ID do produto atual para evitar loops e inconsistências
  const [currentWorkspaceId, setCurrentProductId] = useState<string | null>(
    () => {
      return loadSelectedProductFromLocal()?.id ?? null;
    },
  );

  // Função personalizada para atualizar o produto atual e incrementar a chave de mudança
  const handleProductChange = (workspace: Workspace | null) => {
    const previousProduct = currentWorkspace;

    setCurrentWorkspace(workspace);
    if (workspace) {
      setCurrentProductId(workspace.id);
    } else {
      setCurrentProductId(null);
    }

    // Track workspace switch (apenas quando troca manualmente, não na inicialização)
    if (previousProduct && workspace && previousProduct.id !== workspace.id) {
      analyticsService.trackEvent("product_switched", {
        from_product_name: previousProduct.name,
        to_product_name: workspace.name,
        product_id: workspace.id,
      });
    }

    // Salvar produto selecionado no localStorage
    saveSelectedProductToLocal(workspace);

    // Incrementa a chave de mudança para forçar re-renderização
    setProductChangeKey((prev) => prev + 1);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const refetchRef = useRef<() => Promise<any>>(() => Promise.resolve());
  const isFirstRender = useRef(true);
  const previousUser = useRef(user);

  // Limpar cache apenas quando houver um logout real (user passa de presente para null)
  useEffect(() => {
    // Se é a primeira renderização, não limpar nada
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousUser.current = user;
      return;
    }

    // Se tínhamos um usuário e agora não temos mais, é um logout
    if (previousUser.current && !user) {
      try {
        localStorage.removeItem(PRODUCTS_CACHE_KEY);
        localStorage.removeItem(SELECTED_PRODUCT_KEY);
      } catch (e) {
        console.error("[WorkspaceContext] Erro ao limpar cache:", e);
      }
    }

    // Atualizar referência
    previousUser.current = user;
  }, [user]);

  const {
    data: allProducts = [],
    isLoading: isLoadingQuery,
    isFetched: isProductsFetched,
    refetch,
  } = useQuery({
    queryKey: ["user-products", user?.id, selectedOrganization?.id],
    queryFn: async () => {
      if (!user) {
        return [];
      }

      // System users: fetch products directly by selected organization.
      // v2 token carrega role='authenticated' (Supabase), não 'superadmin' — legacy
      // verifyToken não reconhece o role, então /api/products cai no branch
      // product_users e retorna só os vínculos do user. Rotear para v2 quando flag on.
      if (isSuperAdmin) {
        if (!selectedOrganization?.id) {
          return [];
        }
        try {
          const products = await get<Workspace[]>(
            `/products?organization_id=${selectedOrganization.id}`,
          );
          saveProductsToLocalCache(products);
          return products;
        } catch (error) {
          console.error(
            "[WorkspaceContext] Erro ao carregar produtos (system):",
            error,
          );
          return loadProductsFromLocalCache() || [];
        }
      }

      try {
        const baseUrl = API_CONFIG.API_ROOT_URL;
        const endpoint = "/auth/me";
        const validateResponse = await getApi<any>(`${baseUrl}${endpoint}`);

        // Normalizar envelope: legacy = { success, data: { user } }, v2 = User DTO flat
        // (o /auth/me do api retorna o DTO na raiz, com productIds — NÃO { user }).
        const validatedUser = validateResponse;

        if (validatedUser?.productIds) {
          const productIds: string[] = validatedUser.productIds;

          if (productIds.length === 0) {
            return [];
          }

          // Buscar detalhes para cada produto autorizado
          const productsDetails = await Promise.all(
            productIds.map(async (workspaceId: string) => {
              try {
                const workspaceData =
                  await WorkspaceService.getById(workspaceId);
                return workspaceData;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } catch (err: any) {
                console.error(
                  `[WorkspaceContext] Erro ao buscar detalhes do produto ${workspaceId}:`,
                  err,
                );

                // Se o erro é 403 (sem assinatura), criar um produto básico
                // pois o produto existe, apenas está bloqueado
                if (err?.message?.includes("403")) {
                  return {
                    id: workspaceId,
                    name: "Workspace (Subscription Required)",
                    description: "",
                    organization_id: "",
                  } as Workspace;
                }

                return null;
              }
            }),
          );

          // Filtrar apenas produtos válidos (não null)
          const validProducts = productsDetails.filter(
            (workspace: Workspace | null): workspace is Workspace =>
              workspace !== null,
          );

          // `productIds` do /auth/me abrange TODAS as organizações do usuário
          // (AB#84509). O que a sessão mostra é apenas a organização ativa — sem este
          // recorte, workspaces de outra organização apareceriam misturados na sidebar.
          // Enquanto a organização ativa não resolve, a lista fica vazia em vez de
          // vir inteira: devolver tudo mostraria, por um instante, workspaces de
          // outra organização — exatamente o que este recorte existe para impedir.
          // Superadmin é a exceção, porque opera legitimamente fora de organização.
          // Correspondência ESTRITA. Aceitar workspace sem organização abria uma
          // brecha: o fallback de 403 acima nasce com `organization_id: ''`, o que
          // o fazia aparecer em TODAS as organizações. O custo é que um workspace
          // bloqueado por assinatura deixa de aparecer — preferível a exibi-lo na
          // organização errada, e ele não traz a organização a que pertence.
          //
          // Sem organização ativa a lista fica vazia: devolvê-la inteira mostraria
          // workspaces de outra organização por um instante. Superadmin não passa
          // por aqui — retorna no bloco lá em cima.
          const activeOrgId = selectedOrganization?.id;
          const scoped = activeOrgId
            ? validProducts.filter((w) => w.organization_id === activeOrgId)
            : [];

          // Salvar produtos no cache local
          saveProductsToLocalCache(scoped);

          return scoped;
        } else {
          // Não foi possível obter produtos do token

          // Tentar usar o cache local
          const cachedProducts = loadProductsFromLocalCache();
          if (cachedProducts && cachedProducts.length > 0) {
            return cachedProducts;
          }

          return [];
        }
      } catch (error) {
        console.error("[WorkspaceContext] Erro ao carregar produtos:", error);

        // Em caso de erro, tentar usar o cache local
        const cachedProducts = loadProductsFromLocalCache();
        if (cachedProducts && cachedProducts.length > 0) {
          return cachedProducts;
        }

        return [];
      }
    },
    enabled: isSuperAdmin ? !!user && !!selectedOrganization?.id : !!user,
    // Existe um placeholder porque, sem ele, qualquer troca de chave leva `isLoading`
    // a true e o roteador substitui a aplicação inteira pela tela de carregamento —
    // remontando tudo e devolvendo a sidebar recolhida. Para quem clica, parece que a
    // página recarregou.
    //
    // Mas o placeholder NÃO pode ser a lista anterior quando a organização muda: seria
    // exibir workspaces de outra organização, exatamente o que esta entrega existe para
    // impedir. Aí o placeholder é a lista VAZIA — sem remontagem e sem dado alheio.
    // A comparação inclui o USUÁRIO, não só a organização: sair e outra pessoa entrar
    // sem recarregar a página troca o usuário na chave mas pode manter a mesma
    // organização, e aí o placeholder entregaria a lista de quem saiu.
    placeholderData: (previous: Workspace[] | undefined, previousQuery) => {
      const [, previousUserId, previousOrgId] = (previousQuery?.queryKey ??
        []) as [string?, string?, string?];
      const sameSession =
        previousUserId === user?.id &&
        previousOrgId === selectedOrganization?.id;
      return sameSession ? previous : [];
    },
    staleTime: 300000, // 5 minutos de stale time
    refetchOnWindowFocus: false,
  });

  // Produtos carregados do validate-token já vêm filtrados por autorização do usuário
  const products = allProducts;

  // Para superadmin sem org selecionada, "platform" scope é estado válido (não loading).
  // A query fica disabled e não há produtos a carregar.
  const isLoading = isLoadingQuery;

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  // Função para recarregar produtos do usuário
  const reloadUserProducts = async () => {
    setIsReloading(true);

    // Limpa o cache local para garantir que pegue dados atualizados
    try {
      localStorage.removeItem(PRODUCTS_CACHE_KEY);
    } catch (e) {
      console.error("[WorkspaceContext] Erro ao limpar cache de produtos:", e);
    }

    // Refetch usará getCurrentOrganizationProducts (1 única requisição)
    await refetch();

    // Pequeno delay para garantir que a UI atualize
    setTimeout(() => {
      setIsReloading(false);
    }, 500);
  };

  // Atualizar produto atual quando a lista de produtos ou a organização mudar
  useEffect(() => {
    // Não decidir seleção/limpeza enquanto a lista de produtos ainda está
    // carregando ou nem chegou a ser buscada (ex: superadmin no refresh, com a
    // query desabilitada até a org ser restaurada). currentWorkspace foi
    // hidratado do localStorage e deve permanecer até termos a lista real —
    // caso contrário a seleção seria zerada e o usuário cairia na dashboard da
    // organização. (BUG #81698)
    if (isLoadingQuery || !isProductsFetched) {
      return;
    }

    // Se temos produtos mas nenhum produto atual está selecionado ou o produto selecionado não está mais na lista
    if (products && products.length > 0 && !hasInitialized) {
      setHasInitialized(true);

      // Tentar restaurar o produto salvo do localStorage (se ainda válido)
      const savedProduct = loadSelectedProductFromLocal();
      const savedProductIsValid =
        savedProduct &&
        products.some((p: Workspace) => p.id === savedProduct.id);

      if (savedProductIsValid) {
        setCurrentWorkspace(savedProduct);
        setCurrentProductId(savedProduct.id);
      } else if (isUserOnly) {
        // user role: auto-seleciona o primeiro produto disponível (sem visão da organização)
        setCurrentWorkspace(products[0]);
        setCurrentProductId(products[0].id);
        saveSelectedProductToLocal(products[0]);
      } else {
        // admin/superadmin sempre cai em visão da organização primeiro
        setCurrentWorkspace(null);
        setCurrentProductId(null);
        localStorage.removeItem(SELECTED_PRODUCT_KEY);
      }
    } else if (products && products.length > 0 && hasInitialized) {
      // Após inicialização, apenas validar se o produto atual ainda é válido
      const currentIsValid =
        currentWorkspaceId &&
        products.some((p: Workspace) => p.id === currentWorkspaceId);

      if (!currentIsValid && currentWorkspace) {
        // Se o produto atual não é mais válido, limpar seleção
        setCurrentWorkspace(null);
        setCurrentProductId(null);
        saveSelectedProductToLocal(null);
      }
    } else if (products.length === 0 && currentWorkspace) {
      // Se não há produtos disponíveis, limpar seleção atual
      setCurrentWorkspace(null);
      setCurrentProductId(null);
      saveSelectedProductToLocal(null);
      setHasInitialized(false);
    }
  }, [
    products,
    currentWorkspaceId,
    currentWorkspace,
    hasInitialized,
    isUserOnly,
    isLoadingQuery,
    isProductsFetched,
  ]);

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        setCurrentWorkspace: handleProductChange, // Usa a nova função personalizada
        products,
        isLoading,
        isReloading,
        hasInitialized,
        productChangeKey,
        reloadUserProducts,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}
